import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  editProduct,
  addNewProduct,
  deleteListItem,
  updateOptionListItem,
  editProductOption,
  addOptionListItem,
  saveProductOption,
} from "../../webService/webService";
import { queryKeys } from "../queryKeys";

/**
 * `product` carries its own `id`/`storeId` (see EditProduct.jsx/AddProduct.jsx),
 * so the mutation invalidates that store's catalog directly — the merchant
 * catalog list (Catalog.jsx/Products.jsx) picks up the change on next visit
 * instead of showing stale data after the edit-then-navigate-back flow
 * (previously relied on the destination screen's own mount-effect refetch).
 */
export const useEditProduct = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: ({ product, token }) => editProduct(product, token),
    onSuccess: (data, variables, ...rest) => {
      const { product } = variables;
      queryClient.invalidateQueries({ queryKey: queryKeys.catalog.byStore(product.storeId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.products.detail(product.id) });
      options.onSuccess?.(data, variables, ...rest);
    },
  });
};

export const useAddNewProduct = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: ({ product, token }) => addNewProduct(product, token),
    onSuccess: (data, variables, ...rest) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.catalog.byStore(variables.product.storeId) });
      options.onSuccess?.(data, variables, ...rest);
    },
  });
};

export const useDeleteListItem = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: ({ productId, optionId, id, token }) => deleteListItem(productId, optionId, id, token),
    onSuccess: (data, variables, ...rest) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.products.optionList(variables.productId, variables.optionId),
      });
      options.onSuccess?.(data, variables, ...rest);
    },
  });
};

export const useUpdateOptionListItem = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: ({ productId, optionId, id, item, token }) =>
      updateOptionListItem(productId, optionId, id, item, token),
    onSuccess: (data, variables, ...rest) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.products.optionList(variables.productId, variables.optionId),
      });
      options.onSuccess?.(data, variables, ...rest);
    },
  });
};

export const useEditProductOption = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: ({ option, pid, oid, token }) => editProductOption(option, pid, oid, token),
    onSuccess: (data, variables, ...rest) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.products.options(variables.pid) });
      queryClient.invalidateQueries({
        queryKey: queryKeys.products.optionList(variables.pid, variables.oid),
      });
      options.onSuccess?.(data, variables, ...rest);
    },
  });
};

export const useAddOptionListItem = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: ({ items, pid, oid, token }) => addOptionListItem(items, pid, oid, token),
    onSuccess: (data, variables, ...rest) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.products.optionList(variables.pid, variables.oid),
      });
      options.onSuccess?.(data, variables, ...rest);
    },
  });
};

export const useSaveProductOption = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: ({ option, pid, token }) => saveProductOption(option, pid, token),
    onSuccess: (data, variables, ...rest) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.products.options(variables.pid) });
      options.onSuccess?.(data, variables, ...rest);
    },
  });
};
